import card_1 from "../../../../assets/images/trucheck/card_1.jpg";
import card_2 from "../../../../assets/images/trucheck/card_2.jpg";
import card_3 from "../../../../assets/images/trucheck/card_3.jpg";
import card_4 from "../../../../assets/images/trucheck/card_4.jpg";

/* ============================================================
   PAGE TITLE
============================================================ */

function PageTitle() {
    return (
        <div className="w-full bg-white px-[30px] pt-[60px] pb-[10px] max-[600px]:px-[18px] max-[600px]:pt-10">
            <h1 className="max-w-[1200px] mx-auto text-center m-0 text-[#102a56] font-['Montserrat',Arial,sans-serif] text-[40px] max-[900px]:text-[32px] max-[600px]:text-[26px] leading-[1.2] font-bold">
                How TruCheck Supports Every Stage
            </h1>
        </div>
    );
}

/* ============================================================
   SECTION 1: IMAGE CARDS
============================================================ */

const imageCards = [
    {
        title: "The Foundation",
        image: card_1,
    },
    {
        title: "The Interview Stage",
        image: card_2,
    },
    {
        title: "HR Onboarding",
        image: card_3,
    },
    {
        title: "Verification During Project Meetings",
        image: card_4,
    },
];

function CardItem({ card }) {
    return (
        <div className="bg-[#eaf5fc] border border-[#d8e8f4] rounded-[14px] max-[600px]:rounded-[11px] overflow-hidden shadow-[0_8px_22px_rgba(16,42,86,0.08)] transition-[transform,box-shadow] duration-300 ease-in-out hover:-translate-y-[5px] hover:shadow-[0_14px_30px_rgba(16,42,86,0.15)]">
            {/* CARD HEADING */}
            <div className="bg-[#1670b4] text-white px-5 py-4 max-[600px]:px-[15px] max-[600px]:py-[13px] text-center">
                <h3 className="m-0 text-white font-['Montserrat',Arial,sans-serif] text-[24px] max-[900px]:text-[20px] max-[600px]:text-[18px] leading-[1.25] font-bold">
                    {card.title}
                </h3>
            </div>

            {/* IMAGE AREA */}
            <div className="p-5 max-[900px]:p-[15px] max-[600px]:p-3 flex justify-center items-center">
                <img
                    src={card.image}
                    alt={card.title}
                    width={400}
                    height={400}
                    className="block w-[400px] h-[400px] max-w-full object-cover rounded-[10px] max-[900px]:w-full max-[900px]:h-auto max-[900px]:aspect-square max-[600px]:rounded-lg"
                />
            </div>
        </div>
    );
}

function ImageCardsSection() {
    return (
        <section className="w-full bg-white px-[30px] py-[60px] max-[600px]:px-[18px] max-[600px]:py-10">
            <div className="w-full max-w-[1200px] mx-auto flex flex-col gap-[18px] max-[900px]:gap-[14px]">

                {/* TOP ROW — cards 1 & 2 */}
                <div className="grid grid-cols-2 gap-[18px] max-[900px]:gap-[14px] max-[600px]:grid-cols-1">
                    <CardItem card={imageCards[0]} />
                    <CardItem card={imageCards[1]} />
                </div>

                {/* CONTINUOUS USAGE LABEL */}
                <div className="flex items-center gap-4 py-[6px]">
                    <div className="flex-1 h-px bg-[#d0dff0]" />
                    <span className="text-[#1670b4] text-2xl max-[600px]:text-lg font-semibold tracking-[0.04em] font-['Montserrat',Arial,sans-serif] whitespace-nowrap">
                        Continuous Verified Identity
                    </span>
                    <div className="flex-1 h-px bg-[#d0dff0]" />
                </div>

                {/* BOTTOM ROW — cards 4 & 3 */}
                <div className="grid grid-cols-2 gap-[18px] max-[900px]:gap-[14px] max-[600px]:grid-cols-1">

                    <CardItem card={imageCards[2]} />
                    <CardItem card={imageCards[3]} />
                </div>

            </div>
        </section>
    );
}

/* ============================================================
   SECTION 2: ONE CARD, EVERY STAGE
============================================================ */

const stages = [
    {
        number: "01",
        title: "Candidate Submission",
        text: "Share the candidate's TruCheck™ Card with the client alongside the candidate submission.",
        placement:
            "col-[1] row-[1] max-[900px]:col-[1] max-[900px]:row-[2] max-[600px]:order-2",
    },
    {
        number: "02",
        title: "Interview",
        text: "Use the same card to help confirm that the person attending the interview matches the verified identity.",
        placement:
            "col-[3] row-[1] max-[900px]:col-[2] max-[900px]:row-[2] max-[600px]:order-3",
    },
    {
        number: "03",
        title: "HR Onboarding",
        text: "Reference the same TruCheck™ Card during onboarding to help maintain identity continuity.",
        placement:
            "col-[1] row-[2] max-[900px]:col-[1] max-[900px]:row-[3] max-[600px]:order-4",
    },
    {
        number: "04",
        title: "Project Deployment & Execution",
        text: "Keep the card available during project execution to help maintain the connection between the verified individual and the person working on the project.",
        placement:
            "col-[3] row-[2] max-[900px]:col-[2] max-[900px]:row-[3] max-[600px]:order-5",
    },
];

const cardInfo = [
    { label: "Identity", value: "Verified" },
    { label: "Employability", value: "Validated" },
    { label: "Verification", value: "Complete" },
    { label: "Status", value: "Clear" },
];

function Stage({ number, title, text, placement }) {
    return (
        <div
            className={`relative z-[2] bg-white border border-[#e2eaf5] rounded-[18px] px-6 py-[25px] min-h-[145px] shadow-[0_8px_25px_rgba(16,42,86,0.06)] transition-all duration-300 ease-in-out hover:-translate-y-[5px] hover:shadow-[0_14px_35px_rgba(16,42,86,0.12)] hover:border-[#b9d0f1] max-[600px]:w-full max-[600px]:min-h-0 max-[600px]:p-[21px] ${placement}`}
        >
            <div className="flex items-center gap-[13px] mb-[14px]">
                <div className="w-[38px] h-[38px] flex-none rounded-full flex items-center justify-center bg-[#eaf2ff] text-[#0047bb] text-[14px] font-bold">
                    {number}
                </div>
                <h3 className="m-0 text-[#102a56] text-[17px] leading-[1.3] font-semibold">
                    {title}
                </h3>
            </div>
            <p className="m-0 text-[#66758a] text-[13px] leading-[1.65]">{text}</p>
        </div>
    );
}

function TruCheckCard() {
    return (
        <div className="relative z-[4] col-[2] row-[1/span_2] flex justify-center items-center max-[900px]:col-[1/span_2] max-[900px]:row-[1] max-[600px]:order-1 max-[600px]:w-full max-[600px]:mb-2.5">
            <div className="w-full max-w-[350px] max-[600px]:max-w-[340px] bg-white rounded-[20px] overflow-hidden border border-[#dce6f4] shadow-[0_18px_50px_rgba(16,42,86,0.16)]">

                {/* Card top */}
                <div className="px-[25px] pt-[23px] pb-[18px] bg-gradient-to-br from-white to-[#f2f6fd] border-b border-[#e3ebf6]">
                    <div className="flex items-center gap-[9px] mb-[22px]">
                        <div className="w-[38px] h-[38px] rounded-full bg-[#0047bb] flex items-center justify-center text-white text-[19px] font-bold">
                            ✓
                        </div>
                        <div>
                            <div className="text-[#0047bb] text-[21px] font-semibold tracking-[-0.5px]">
                                TruCheck™
                            </div>
                            <span className="block text-[#7b8798] text-[7px] tracking-[1.2px] mt-0.5">
                                TRUSTED IDENTITY
                            </span>
                        </div>
                    </div>

                    <div className="text-center text-[#0047bb] text-[17px] font-medium">
                        Identity &amp; Employability Check
                    </div>
                </div>

                {/* Card body */}
                <div className="px-[25px] py-[22px]">
                    <div className="inline-flex items-center gap-[7px] px-[11px] py-1.5 rounded-[30px] bg-[#e9fbf4] text-[#00966d] border border-[#b8efd9] text-[10px] font-semibold mb-[18px]">
                        <span className="w-[7px] h-[7px] rounded-full bg-[#00b47d]" />
                        Verification Clear
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        {cardInfo.map((info) => (
                            <div key={info.label}>
                                <span className="block mb-[5px] text-[#8291a7] text-[8px] tracking-[1.3px] uppercase font-semibold">
                                    {info.label}
                                </span>
                                <span className="text-[#18345e] text-[11px] font-medium">
                                    {info.value}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Card footer */}
                <div className="px-[25px] py-[15px] bg-[#f4f7fc] border-t border-[#e4ebf5] flex justify-between items-center">
                    <span className="text-[#8291a7] text-[8px] tracking-[1px]">
                        REFERENCE • TRUCHECK
                    </span>
                    <div
                        className="w-[45px] h-[45px] border-[5px] border-white"
                        style={{
                            background:
                                "linear-gradient(90deg, #0047bb 15%, transparent 15%, transparent 30%, #0047bb 30%, #0047bb 45%, transparent 45%), linear-gradient(#0047bb 15%, transparent 15%, transparent 30%, #0047bb 30%, #0047bb 45%, transparent 45%)",
                            backgroundSize: "13px 13px",
                        }}
                    />
                </div>
            </div>
        </div>
    );
}

function JourneySection() {
    return (
        <section className="w-full bg-[#f5f8fd] font-['Montserrat',Arial,sans-serif] px-[30px] py-[90px] max-[900px]:px-6 max-[900px]:py-[70px] max-[600px]:px-[18px] max-[600px]:py-[60px]">
            <div className="max-w-[1200px] mx-auto">

                {/* SECTION HEADER */}
                <div className="text-center max-w-[760px] mx-auto mb-[65px] max-[600px]:mb-10">
                    <span className="inline-block mb-[14px] text-[12px] font-bold tracking-[2px] uppercase text-[#0047bb]">
                        One Verified Identity
                    </span>

                    <h2 className="m-0 mb-[18px] text-[#102a56] text-[38px] max-[600px]:text-[29px] leading-[1.2] font-semibold">
                        One TruCheck™ Card. Every Stage.
                    </h2>

                    <p className="mx-auto max-w-[700px] text-[#5f6f86] text-[16px] max-[600px]:text-[14px] leading-[1.7] max-[600px]:leading-[1.6]">
                        Keep the same verified identity connected throughout the candidate journey —
                        from submission and interviews through HR onboarding and project execution.
                    </p>
                </div>

                {/* JOURNEY MAP */}
                <div className="relative grid grid-cols-[1fr_360px_1fr] grid-rows-[auto_auto] gap-x-[45px] gap-y-[45px] items-center max-[900px]:grid-cols-[1fr_1fr] max-[900px]:grid-rows-[auto_auto_auto] max-[900px]:gap-x-[25px] max-[900px]:gap-y-[30px] max-[600px]:flex max-[600px]:flex-col max-[600px]:gap-[18px]">

                    {/* Connecting line */}
                    <div className="absolute left-[14%] right-[14%] top-1/2 h-0.5 bg-[#c9daf3] z-0 max-[900px]:hidden" />

                    {stages.map((stage) => (
                        <Stage key={stage.number} {...stage} />
                    ))}

                    <TruCheckCard />
                </div>

                {/* BOTTOM MESSAGE */}
                <div className="max-w-[760px] mx-auto mt-[60px] max-[600px]:mt-10 px-7 py-[22px] max-[600px]:p-5 text-center bg-white border border-[#dce6f4] rounded-[14px]">
                    <p className="m-0 text-[#52647d] text-[14px] max-[600px]:text-[13px] leading-[1.7]">
                        <strong className="text-[#102a56]">One card. One verified identity.</strong>{" "}
                        Keep the TruCheck™ Card accessible throughout the candidate
                        lifecycle so verification can be referenced whenever needed.
                    </p>
                </div>

            </div>
        </section>
    );
}

/* ============================================================
   FULL PAGE
============================================================ */

export default function TruCheckInfographic() {
    return (
        <main className="min-h-screen w-full bg-white">
            <PageTitle />
            <ImageCardsSection />
            <JourneySection />
        </main>
    );
}
